// src/redux/slices/usersSlice.js
import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { CustomerUseApi } from '../../services/userCustomer';

// Thunk to fetch all users and summary data
export const fetchUsers = createAsyncThunk(
  'users/fetchUsers',
  async (_, { rejectWithValue }) => {
    try {
      const data = await CustomerUseApi.getListOfCustomerAccounts();
      return {
        list_of_users: data.list_of_user_accounts,
        customer_accounts: {
          total_customer: data.total_customer,
          active: data.active,
          growth_status: data.growth_status,
        },
      };
    } catch (err) {
      console.error("API Error:", err);
      return rejectWithValue(err.response?.data || 'Something went wrong');
    }
  }
);

// Thunk to search and filter users
export const searchAndFilterCustomerAccount = createAsyncThunk(
  'users/searchAndFilterCustomerAccount',
  async ({ searchTerm, orderHistory = null, accountStatus = 'active' }, { rejectWithValue }) => {
    try {
      const data = await CustomerUseApi.searchAndFilterCustomerAccount(searchTerm, orderHistory, accountStatus);
      return data;
    } catch (err) {
      console.error("API Error:", err);
      return rejectWithValue(err.response?.data || 'Something went wrong');
    }
  }
);

export const fetchSingleCustomerAccountDetail = createAsyncThunk(
  'users/fetchSingleCustomerAccountDetail',
  async (userId, { rejectWithValue }) => {
    try {
      const data = await CustomerUseApi.getSingleCustomerAccountDetail(userId);
      return data;
    } catch (err) {
      console.error("API Error:", err);
      return rejectWithValue(err.response?.data || 'Something went wrong');
    }
  }
);

export const updateCustomerAccountDetail = createAsyncThunk(
  'users/updateCustomerAccountDetail',
  async ({ userId, formData }, { rejectWithValue }) => {
    try {
      const data = await CustomerUseApi.UpdateCustomerAccountDetail(userId, formData);
      return data;
    } catch (err) {
      console.error("API Error:", err);
      return rejectWithValue(err.response?.data || 'Something went wrong');
    }
  }
);

// Enhanced registration thunk with better error handling
export const registerNewAccount = createAsyncThunk(
  'users/registerNewAccount',
  async (formData, { rejectWithValue }) => {
    try {
      const data = await CustomerUseApi.registerNewAccount(formData);
      return data;
    } catch (err) {
      console.error("Registration API Error:", err);
      
      // Enhanced error handling for registration
      if (err.response?.data) {
        return rejectWithValue(err.response.data);
      } else if (err.message) {
        return rejectWithValue({ message: err.message });
      } else {
        return rejectWithValue({ message: 'Registration failed. Please try again.' });
      }
    }
  }
);

export const userNewsLetterApi = createAsyncThunk(
  'users/userNewsLetterApi',
  async (email, { rejectWithValue }) => {
    try {
      const data = await CustomerUseApi.userNewsLetterApi(email);
      return data;
    } catch (err) {
      console.error("API Error:", err);
      return rejectWithValue(err.response?.data || 'Something went wrong');
    }
  }
);

export const deleteCustomerAccounts = createAsyncThunk(
  'users/deleteCustomerAccounts',
  async (userIds, { rejectWithValue }) => {
    try {
      const data = await CustomerUseApi.deleteCustomerAccounts(userIds);
      return { userIds, ...data };
    } catch (err) {
      console.error("API Error:", err);
      return rejectWithValue(err.response?.data || 'Something went wrong');
    }
  }
);

export const toggleUserAccountsStatus = createAsyncThunk(
  'users/toggleUserAccountsStatus',
  async (userIds, { rejectWithValue }) => {
    try {
      const data = await CustomerUseApi.toggleUserAccountsStatus(userIds);
      return { userIds, ...data };
    } catch (err) {
      console.error("API Error:", err);
      return rejectWithValue(err.response?.data || 'Something went wrong');
    }
  }
);

const usersSlice = createSlice({
  name: 'users',
  initialState: {
    loading: false,
    isSearching: false,
    error: null,
    searchTerm: null,
    orderHistory: null,
    accountStatus: 'active',
    singleUser: null,
    singleUserLoading: false,
    data: {
      list_of_users: [],
      customer_accounts: {},
    },
    newsLetterSubscription: null,
    
    // Registration specific states
    registrationLoading: false,
    registrationError: null,
    registrationSuccess: false,
  },
  reducers: {
    setSearchTerm: (state, action) => {
      state.searchTerm = action.payload;
    },
    clearSearch: (state) => {
      state.searchTerm = null;
      state.orderHistory = null;
      state.accountStatus = 'active';
      state.isSearching = false;
    },
    clearSingleUser: (state) => {
      state.singleUser = null;
    },
    // Add registration-specific actions
    clearRegistrationError: (state) => {
      state.registrationError = null;
    },
    clearRegistrationSuccess: (state) => {
      state.registrationSuccess = false;
    },
    resetRegistrationState: (state) => {
      state.registrationLoading = false;
      state.registrationError = null;
      state.registrationSuccess = false;
    },
  },
  extraReducers: (builder) => {
    builder
      // Handle fetchUsers
      .addCase(fetchUsers.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchUsers.fulfilled, (state, action) => {
        state.loading = false;
        state.data = action.payload;
      })
      .addCase(fetchUsers.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // Handle searchAndFilterCustomerAccount
      .addCase(searchAndFilterCustomerAccount.pending, (state) => {
        state.isSearching = true;
        state.error = null;
      })
      .addCase(searchAndFilterCustomerAccount.fulfilled, (state, action) => {
        state.isSearching = false;
        state.data.list_of_users = action.payload;
      })
      .addCase(searchAndFilterCustomerAccount.rejected, (state, action) => {
        state.isSearching = false;
        state.error = action.payload;
      })

      // Handle fetchSingleCustomerAccountDetail
      .addCase(fetchSingleCustomerAccountDetail.pending, (state) => {
        state.singleUserLoading = true;
        state.error = null;
      })
      .addCase(fetchSingleCustomerAccountDetail.fulfilled, (state, action) => {
        state.singleUserLoading = false;
        state.singleUser = action.payload;
      })
      .addCase(fetchSingleCustomerAccountDetail.rejected, (state, action) => {
        state.singleUserLoading = false;
        state.error = action.payload;
      })

      // Handle updateCustomerAccountDetail
      .addCase(updateCustomerAccountDetail.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateCustomerAccountDetail.fulfilled, (state, action) => {
        state.loading = false;
        state.singleUser = action.payload;
      })
      .addCase(updateCustomerAccountDetail.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // Enhanced registration handling
      .addCase(registerNewAccount.pending, (state) => {
        state.registrationLoading = true;
        state.registrationError = null;
        state.registrationSuccess = false;
      })
      .addCase(registerNewAccount.fulfilled, (state, action) => {
        state.registrationLoading = false;
        state.registrationError = null;
        state.registrationSuccess = true;
        // Only add to list if we're in admin context, not during signup
        if (state.data.list_of_users) {
          state.data.list_of_users.push(action.payload);
        }
      })
      .addCase(registerNewAccount.rejected, (state, action) => {
        state.registrationLoading = false;
        state.registrationError = action.payload;
        state.registrationSuccess = false;
      })

      // Handle userNewsLetterApi
      .addCase(userNewsLetterApi.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(userNewsLetterApi.fulfilled, (state, action) => {
        state.loading = false;
        state.newsLetterSubscription = action.payload;
      })
      .addCase(userNewsLetterApi.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // Handle deleteCustomerAccounts
      .addCase(deleteCustomerAccounts.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(deleteCustomerAccounts.fulfilled, (state, action) => {
        state.loading = false;
        const deletedIds = action.payload.userIds;
        state.data.list_of_users = state.data.list_of_users.filter(
          user => !deletedIds.includes(user.id)
        );
      })
      .addCase(deleteCustomerAccounts.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // Handle toggleUserAccountsStatus
      .addCase(toggleUserAccountsStatus.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(toggleUserAccountsStatus.fulfilled, (state, action) => {
        state.loading = false;
        const { updated_users } = action.payload;
        if (updated_users && Array.isArray(updated_users)) {
          updated_users.forEach(({ id, new_status }) => {
            const user = state.data.list_of_users.find(u => u.id === id);
            if (user) user.account_status = new_status;
          });
        }
      })
      .addCase(toggleUserAccountsStatus.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });
  },
});

export const { 
  setSearchTerm, 
  clearSearch, 
  clearSingleUser,
  clearRegistrationError,
  clearRegistrationSuccess,
  resetRegistrationState
} = usersSlice.actions;

export default usersSlice.reducer;